import { skipToken } from "@reduxjs/toolkit/query/react";
import { orderTypeConstants } from "constants/order";
import { convertToUTC } from "pages/CreateOrder/utils/helpers";
import {
  buildMatchingOrdersFormValues,
  EMPTY_MATCHING_ORDERS,
  isCompleteFormDate,
} from "pages/CreateOrder/utils/matchingOrders";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useGetMatchingOrdersQuery } from "store/queries/pricing";
import { IMatchingOrdersRequest } from "types/Order/Order";

/**
 * CLE-2769 follow-up. Looks up the patient's earlier live estimates (the deductible-reduction
 * suggestion) as soon as first name, last name, DOB and visit date are on the form, and writes the
 * banner's form values. It used to be a side-output of the price calculation, which only runs
 * once a CPT and the benefits are entered — so the banner waited for a CPT for no reason.
 *
 * Sole writer of `hasMatchingOrders`, `deductibleReductionAmount`, `oopReductionAmount` and
 * `matchingOrdersData`; the Benefits section reads them.
 */
export default function useMatchingOrders() {
  const { watch, setValue } = useFormContext();
  const orderType = watch("orderType");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const dateOfBirth = watch("dateOfBirth");
  const dateOfService = watch("dateOfService");
  const orderId = watch("orderId");

  const request = useMemo<IMatchingOrdersRequest | typeof skipToken>(() => {
    if (
      orderType !== orderTypeConstants.GFE ||
      !firstName ||
      !lastName ||
      !isCompleteFormDate(dateOfBirth) ||
      !isCompleteFormDate(dateOfService)
    ) {
      return skipToken;
    }
    return {
      firstName,
      lastName,
      dateOfBirth: convertToUTC(dateOfBirth),
      dateOfService: convertToUTC(dateOfService),
      // Redo / review edit the same estimate, which must never match itself.
      ...(orderId ? { excludeOrderId: orderId } : {}),
    };
  }, [orderType, firstName, lastName, dateOfBirth, dateOfService, orderId]);

  const { data } = useGetMatchingOrdersQuery(request);

  useEffect(() => {
    const values =
      request === skipToken
        ? EMPTY_MATCHING_ORDERS
        : buildMatchingOrdersFormValues(data?.data);
    setValue("hasMatchingOrders", values.hasMatchingOrders);
    setValue("deductibleReductionAmount", values.deductibleReductionAmount);
    setValue("oopReductionAmount", values.oopReductionAmount);
    setValue("matchingOrdersData", values.matchingOrdersData);
  }, [request, data, setValue]);
}
